import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Save, ChevronDown } from "lucide-react";
import { api } from "@/pages/Admin";
import { ImageUpload } from "@/components/admin/ImageUpload";

const emptyTrack = { cat: "", title: "", artist: "", genre: "", bpm: 128, cover_url: "", spotify_url: "", apple_url: "", youtube_url: "", soundcloud_url: "", beatport_url: "" };
const inputCls = "w-full bg-[#08080c] border border-white/15 px-3 py-2.5 text-sm text-white placeholder:text-slate-600 focus:border-[#00f0ff]";

const fields = [
  ["cat", "Catalogue # (e.g. RAN-007)"], ["title", "Title"], ["artist", "Artist"], ["genre", "Genre"], ["bpm", "BPM"],
  ["spotify_url", "Spotify URL"], ["apple_url", "Apple Music URL"],
  ["youtube_url", "YouTube URL"], ["soundcloud_url", "SoundCloud URL"], ["beatport_url", "Beatport URL"],
];

const TrackForm = ({ value, onChange, onSubmit, submitLabel, testPrefix }) => (
  <form onSubmit={onSubmit} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
    {fields.map(([k, label]) => (
      <div key={k}>
        <label className="block font-mono-tech text-[10px] tracking-[0.2em] uppercase text-slate-500 mb-1.5">{label}</label>
        <input
          data-testid={`${testPrefix}-${k}`}
          required={["cat", "title", "artist", "genre", "bpm"].includes(k)}
          type={k === "bpm" ? "number" : "text"}
          value={value[k]}
          onChange={(e) => onChange({ ...value, [k]: k === "bpm" ? Number(e.target.value) : e.target.value })}
          className={inputCls}
        />
      </div>
    ))}
    <div className="sm:col-span-2">
      <label className="block font-mono-tech text-[10px] tracking-[0.2em] uppercase text-slate-500 mb-1.5">Cover image</label>
      <ImageUpload value={value.cover_url} onChange={(v) => onChange({ ...value, cover_url: v })} testPrefix={`${testPrefix}-cover`} />
    </div>
    <button data-testid={`${testPrefix}-submit`} type="submit" className="sm:col-span-2 flex items-center justify-center gap-2 px-6 py-3 bg-[#00f0ff] text-black font-mono-tech text-xs tracking-[0.2em] uppercase font-bold hover:bg-white transition-colors">
      <Save size={13} /> {submitLabel}
    </button>
  </form>
);

export const TracksManager = () => {
  const [tracks, setTracks] = useState([]);
  const [draft, setDraft] = useState(emptyTrack);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editDraft, setEditDraft] = useState(emptyTrack);

  const load = () => api.get("/tracks").then((r) => setTracks(r.data));
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/tracks", draft);
      toast.success("Track added");
      setDraft(emptyTrack);
      setAdding(false);
      load();
    } catch { toast.error("Could not add track"); }
  };

  const update = async (e) => {
    e.preventDefault();
    try {
      const { id, created_at, ...body } = editDraft;
      await api.put(`/tracks/${editing}`, body);
      toast.success("Track updated");
      setEditing(null);
      load();
    } catch { toast.error("Could not update track"); }
  };

  const remove = async (t) => {
    if (!window.confirm(`Delete ${t.cat} — ${t.title}?`)) return;
    try {
      await api.delete(`/tracks/${t.id}`);
      toast.success("Track deleted");
      load();
    } catch { toast.error("Could not delete track"); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="font-display text-lg font-bold text-white uppercase">Catalogue ({tracks.length})</h2>
        <button data-testid="admin-add-track-toggle" onClick={() => setAdding(!adding)} className="flex items-center gap-2 px-5 py-2.5 bg-[#ff0055] text-white font-mono-tech text-[11px] tracking-[0.2em] uppercase font-bold hover:bg-[#00f0ff] hover:text-black transition-colors">
          <Plus size={13} /> New Track
        </button>
      </div>

      {adding && (
        <div className="bg-[#0c0c12] border border-[#ff0055]/40 p-6">
          <TrackForm value={draft} onChange={setDraft} onSubmit={create} submitLabel="Add to Catalogue" testPrefix="admin-new-track" />
        </div>
      )}

      <div className="space-y-px">
        {tracks.map((t) => (
          <div key={t.id} className="bg-[#0c0c12] border border-white/10">
            <div className="flex items-center gap-4 p-4">
              <img src={t.cover_url} alt="" className="w-12 h-12 object-cover" />
              <div className="flex-1 min-w-0">
                <p className="font-display text-sm font-bold text-white uppercase truncate">{t.title}</p>
                <p className="font-mono-tech text-[10px] tracking-[0.2em] text-slate-500 uppercase">{t.cat} — {t.artist} — {t.bpm} BPM</p>
              </div>
              <button
                data-testid={`admin-edit-track-${t.cat}`}
                onClick={() => { setEditing(editing === t.id ? null : t.id); setEditDraft(t); }}
                className="flex items-center gap-1.5 font-mono-tech text-[10px] tracking-[0.2em] uppercase text-[#00f0ff] hover:text-white transition-colors"
              >
                Edit <ChevronDown size={12} className={editing === t.id ? "rotate-180" : ""} />
              </button>
              <button data-testid={`admin-delete-track-${t.cat}`} onClick={() => remove(t)} aria-label="Delete track" className="text-slate-500 hover:text-[#ff0055] transition-colors">
                <Trash2 size={15} />
              </button>
            </div>
            {editing === t.id && (
              <div className="border-t border-white/10 p-6">
                <TrackForm value={editDraft} onChange={setEditDraft} onSubmit={update} submitLabel="Save Changes" testPrefix="admin-edit-track" />
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
